import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../utils/constant';

const BASE_URL = `${API_URL}/raw-products`;

// Create a new product
export const createProduct = createAsyncThunk('product/createProduct', async (productData, thunkAPI) => {
  try {
    const response = await axios.post(`${BASE_URL}`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Get all products with pagination and filters
export const getProducts = createAsyncThunk('product/getProducts', async ({ companyId, page = 1, limit = 10, filters = {} }, thunkAPI) => {
  try {
    const params = {
      page,
      limit,
      // Product filters
      ...(filters.productName && { productName: filters.productName }),
      ...(filters.categoryId && { categoryId: filters.categoryId }),
      ...(filters.brandId && { brandId: filters.brandId }),
      ...(filters.productValueType && { productValueType: filters.productValueType }),
      ...(filters.sku && { sku: filters.sku }),
      // AvailableVariants filters
      ...(filters.potency && { potency: filters.potency }),
      ...(filters.volume && { volume: filters.volume }),
      // Variation filters
      ...(filters.barcode && { barcode: filters.barcode }),
      ...(filters.serialNo && { serialNo: filters.serialNo }),
      ...(filters.size && { size: filters.size }),
      ...(filters.color && { color: filters.color }),
    };

    const response = await axios.get(`${BASE_URL}/${companyId ?? 4}`, {
      params
    });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Get all products stock
export const getProductsStock = createAsyncThunk('product/getProductsStock', async (_, thunkAPI) => {
  const company = JSON.parse(localStorage.getItem('company'));
  try {
    const response = await axios.get(`${BASE_URL}/stock/${company?.company?.id}`);
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Update an existing product
export const updateProduct = createAsyncThunk('product/updateProduct', async ({ id, productData }, thunkAPI) => {
  try {
    const response = await axios.put(`${BASE_URL}/${id}`, productData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Delete a product
export const deleteProduct = createAsyncThunk('product/deleteProduct', async (id, thunkAPI) => {
  try {
    await axios.delete(`${BASE_URL}/${id}`);
    return { id };
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// Slice definition
const rawproductSlice = createSlice({
  name: 'rawproducts',
  initialState: {
    rawProducts: [],
    stockProducts: [],
    potencies: [],
    volumes: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
    loading: false,
    error: null,
    // Track if filters have been loaded
    filtersLoaded: false,
    // Store current filters in state
    filters: {}
  },
  reducers: {
    // Optional: Add a reducer to reset pagination state
    resetPagination: (state) => {
      state.currentPage = 1;
      state.limit = 10;
    },
    // Set filters and reset to page 1
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.currentPage = 1; // Reset to first page when filters change
    },
    // Clear all filters
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    // Manually refresh potencies and volumes if needed
    refreshFilters: (state) => {
      state.filtersLoaded = false;
      state.potencies = [];
      state.volumes = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle createProduct
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.rawProducts.push(action.payload.product);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Handle getProducts
      .addCase(getProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.rawProducts = action.payload.data;
        if (action.payload.pagination) {
          state.totalCount = action.payload.pagination.totalCount;
          state.totalPages = action.payload.pagination.totalPages;
          state.currentPage = action.payload.pagination.currentPage;
          state.limit = action.payload.pagination.limit;
        }
        
        // Only update potencies and volumes if they haven't been loaded yet
        // and if the API returned them
        if (!state.filtersLoaded && action.payload.potencies && action.payload.volumes) {
          state.potencies = action.payload.potencies;
          state.volumes = action.payload.volumes;
          state.filtersLoaded = true;
        }
      })
      .addCase(getProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Handle getProductsStock
      .addCase(getProductsStock.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProductsStock.fulfilled, (state, action) => {
        state.loading = false;
        state.stockProducts = action.payload;
      })
      .addCase(getProductsStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle updateProduct
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const updatedProduct = action.payload.product;
        const index = state.rawProducts.findIndex((product) => product.id == updatedProduct.id);
        if (index !== -1) {
          state.rawProducts[index] = updatedProduct;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle deleteProduct
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.rawProducts = state.rawProducts.filter((product) => product.id !== action.payload.id);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { resetPagination, setFilters, clearFilters, refreshFilters } = rawproductSlice.actions;
export default rawproductSlice.reducer; 